'use client'

import React, { useState } from 'react'
import styles from './Historicals.module.css'

/**
 * @uiComponent
 * @name Historicals
 * @description The Historicals component provides comprehensive historical stock data analysis and export capabilities.
 * It allows users to configure date ranges, timeframes, and technical indicators (RSI, MACD, Bollinger Bands, EMA)
 * to analyze historical stock performance. Features include customizable indicator parameters and Excel export functionality.
 * @location Historicals plugin in the trading terminal
 * @prop none - No external props, manages internal state for analysis configuration
 */

/**
 * @uiWorkflow
 * @name Download Historical Stock Data with Technical Indicators
 * @description How to export historical stock data with custom technical analysis indicators
 * @platform historicals
 * @step 1 Navigate to the Historicals plugin [component: Historicals] [action: navigate]
 * @step 2 Enter the stock symbol to analyze [component: Historicals] [action: type] [notes: e.g., AAPL, MSFT, PAYTM.NS]
 * @step 3 Select the desired timeframe [component: Historicals] [action: select] [notes: 1m, 5m, 15m, 30m, 1h, 4h, 1d, 1w]
 * @step 4 Choose a duration preset or set custom dates [component: Historicals] [action: configure] [notes: 1m, 3m, 6m, 1y, 3y, 5y, max]
 * @step 5 Enable RSI indicator if desired [component: Historicals] [action: check] [notes: Relative Strength Index]
 * @step 6 Configure RSI period parameter [component: Historicals] [action: adjust] [notes: Default 14 periods]
 * @step 7 Enable MACD indicator if desired [component: Historicals] [action: check] [notes: Moving Average Convergence Divergence]
 * @step 8 Set MACD parameters [component: Historicals] [action: configure] [notes: Fast 12, Slow 26, Signal 9]
 * @step 9 Enable other indicators as needed [component: Historicals] [action: select] [notes: Bollinger Bands, EMA]
 * @step 10 Click download to export Excel file [component: Historicals] [action: click] [notes: Includes all selected indicators]
 */

/**
 * @uiWorkflow
 * @name Configure Advanced Technical Indicator Parameters
 * @description How to customize technical indicator settings for detailed analysis
 * @platform historicals
 * @step 1 Access the Historicals analysis tool [component: Historicals] [action: navigate]
 * @step 2 Set up basic stock symbol and timeframe [component: Historicals] [action: configure]
 * @step 3 Enable Bollinger Bands indicator [component: Historicals] [action: check]
 * @step 4 Adjust Bollinger Bands period [component: Historicals] [action: modify] [notes: Default 20 periods]
 * @step 5 Set standard deviation multiplier [component: Historicals] [action: adjust] [notes: Default 2.0]
 * @step 6 Enable EMA (Exponential Moving Average) [component: Historicals] [action: check]
 * @step 7 Configure EMA period length [component: Historicals] [action: set] [notes: Default 20 periods]
 * @step 8 Fine-tune MACD parameters if enabled [component: Historicals] [action: optimize] [notes: Adjust fast, slow, signal periods]
 * @step 9 Test different parameter combinations [component: Historicals] [action: experiment]
 * @step 10 Download optimized analysis results [component: Historicals] [action: export]
 */

/**
 * @uiWorkflow
 * @name Analyze Different Time Periods for Market Trends
 * @description How to compare stock performance across various time horizons
 * @platform historicals
 * @step 1 Start with your target stock symbol [component: Historicals] [action: input]
 * @step 2 Select short-term duration (1m or 3m) [component: Historicals] [action: select] [notes: Recent performance]
 * @step 3 Enable key indicators for short-term analysis [component: Historicals] [action: configure] [notes: RSI, MACD recommended]
 * @step 4 Download short-term data [component: Historicals] [action: export]
 * @step 5 Change duration to long-term (3y or 5y) [component: Historicals] [action: modify]
 * @step 6 Adjust timeframe to daily or weekly [component: Historicals] [action: select] [notes: Better for long-term trends]
 * @step 7 Enable trend indicators [component: Historicals] [action: configure] [notes: EMA, Bollinger Bands]
 * @step 8 Download long-term analysis [component: Historicals] [action: export]
 * @step 9 Compare short vs long-term trends [component: Historicals] [action: analyze]
 * @step 10 Identify optimal entry/exit points [component: Historicals] [action: strategize]
 */

export default function Historicals() {
  // Form state
  const [symbol, setSymbol] = useState('BTCUSDT')
  const [timeFrame, setTimeFrame] = useState('1d')
  const [startDate, setStartDate] = useState('2020-01-01')
  const [endDate, setEndDate] = useState('2025-01-01')
  const [duration, setDuration] = useState('1y')
  // Indicator selections and parameters
  const [rsiSelected, setRsiSelected] = useState(false)
  const [rsiPeriod, setRsiPeriod] = useState(14)
  const [macdSelected, setMacdSelected] = useState(false)
  const [macdFastPeriod, setMacdFastPeriod] = useState(12)
  const [macdSlowPeriod, setMacdSlowPeriod] = useState(26)
  const [macdSignalPeriod, setMacdSignalPeriod] = useState(9)
  const [bollingerSelected, setBollingerSelected] = useState(false)
  const [bollingerPeriod, setBollingerPeriod] = useState(20)
  const [bollingerStdDev, setBollingerStdDev] = useState(2)
  const [emaSelected, setEmaSelected] = useState(false)
  const [emaPeriod, setEmaPeriod] = useState(20)

  // Helper function to set date based on duration
  const handleDurationChange = (newDuration: string) => {
    setDuration(newDuration)
    const end = new Date()
    let start = new Date()

    switch (newDuration) {
      case '1m':
        start.setMonth(end.getMonth() - 1)
        break
      case '3m':
        start.setMonth(end.getMonth() - 3)
        break
      case '6m':
        start.setMonth(end.getMonth() - 6)
        break
      case '1y':
        start.setFullYear(end.getFullYear() - 1)
        break
      case '3y':
        start.setFullYear(end.getFullYear() - 3)
        break
      case '5y':
        start.setFullYear(end.getFullYear() - 5)
        break
      case 'max':
        start = new Date('2000-01-01')
        break
      default:
        start.setFullYear(end.getFullYear() - 1)
    }

    setStartDate(start.toISOString().split('T')[0])
    setEndDate(end.toISOString().split('T')[0])
  }

  const handleDownload = async () => {
    // Prepare indicator labels with the parameters so that Excel headers show the signal data.
    const indicatorLabels = {
      rsi: rsiSelected ? `RSI(${rsiPeriod})` : null,
      macd: macdSelected
        ? `MACD(${macdFastPeriod},${macdSlowPeriod},${macdSignalPeriod})`
        : null,
      bollinger: bollingerSelected
        ? `Bollinger(${bollingerPeriod},${bollingerStdDev})`
        : null,
      ema: emaSelected ? `EMA(${emaPeriod})` : null
    }

    const params = {
      symbol,
      timeFrame,
      startDate,
      endDate,
      indicators: {
        rsi: rsiSelected ? { period: rsiPeriod } : null,
        macd: macdSelected
          ? {
              fastPeriod: macdFastPeriod,
              slowPeriod: macdSlowPeriod,
              signalPeriod: macdSignalPeriod
            }
          : null,
        bollinger: bollingerSelected
          ? {
              period: bollingerPeriod,
              stdDev: bollingerStdDev
            }
          : null,
        ema: emaSelected
          ? {
              period: emaPeriod
            }
          : null
      },
      indicatorLabels
    }

    const res = await fetch('/api/historicals', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params)
    })

    if (res.ok) {
      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${symbol}_historicals.xlsx`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    }
  }

  return (
    <div className={styles.container}>
      <h2>Historicals Data Export</h2>
      <div className={styles.formGroup}>
        <label>
          Symbol/Ticker:
          <input
            type="text"
            value={symbol}
            onChange={e => setSymbol(e.target.value)}
            placeholder="e.g. AAPL, MSFT, BTC-USD"
          />
        </label>

        <label>
          Time Frame:
          <select
            value={timeFrame}
            onChange={e => setTimeFrame(e.target.value)}
          >
            <option value="1m">1 Minute</option>
            <option value="5m">5 Minutes</option>
            <option value="15m">15 Minutes</option>
            <option value="30m">30 Minutes</option>
            <option value="1h">1 Hour</option>
            <option value="1d">Daily</option>
            <option value="1wk">Weekly</option>
            <option value="1mo">Monthly</option>
          </select>
        </label>

        <label>
          Duration:
          <select
            value={duration}
            onChange={e => handleDurationChange(e.target.value)}
          >
            <option value="1m">1 Month</option>
            <option value="3m">3 Months</option>
            <option value="6m">6 Months</option>
            <option value="1y">1 Year</option>
            <option value="3y">3 Years</option>
            <option value="5y">5 Years</option>
            <option value="max">Max</option>
            <option value="custom">Custom</option>
          </select>
        </label>

        {duration === 'custom' && (
          <>
            <label>
              Start Date:
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
              />
            </label>
            <label>
              End Date:
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
              />
            </label>
          </>
        )}

        <div className={styles.indicatorsSection}>
          <h3>Technical Indicators</h3>

          <div className={styles.indicatorGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={rsiSelected}
                onChange={e => setRsiSelected(e.target.checked)}
              />
              RSI (Relative Strength Index)
            </label>
            {rsiSelected && (
              <div className={styles.indicatorParams}>
                <label>
                  Period:
                  <input
                    type="number"
                    min="2"
                    max="50"
                    value={rsiPeriod}
                    onChange={e => setRsiPeriod(Number(e.target.value))}
                  />
                </label>
              </div>
            )}
          </div>

          <div className={styles.indicatorGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={macdSelected}
                onChange={e => setMacdSelected(e.target.checked)}
              />
              MACD (Moving Average Convergence Divergence)
            </label>
            {macdSelected && (
              <div className={styles.indicatorParams}>
                <label>
                  Fast Period:
                  <input
                    type="number"
                    min="2"
                    max="50"
                    value={macdFastPeriod}
                    onChange={e => setMacdFastPeriod(Number(e.target.value))}
                  />
                </label>
                <label>
                  Slow Period:
                  <input
                    type="number"
                    min="2"
                    max="100"
                    value={macdSlowPeriod}
                    onChange={e => setMacdSlowPeriod(Number(e.target.value))}
                  />
                </label>
                <label>
                  Signal Period:
                  <input
                    type="number"
                    min="2"
                    max="50"
                    value={macdSignalPeriod}
                    onChange={e => setMacdSignalPeriod(Number(e.target.value))}
                  />
                </label>
              </div>
            )}
          </div>

          <div className={styles.indicatorGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={bollingerSelected}
                onChange={e => setBollingerSelected(e.target.checked)}
              />
              Bollinger Bands
            </label>
            {bollingerSelected && (
              <div className={styles.indicatorParams}>
                <label>
                  Period:
                  <input
                    type="number"
                    min="2"
                    max="50"
                    value={bollingerPeriod}
                    onChange={e => setBollingerPeriod(Number(e.target.value))}
                  />
                </label>
                <label>
                  Standard Deviation:
                  <input
                    type="number"
                    min="1"
                    max="5"
                    step="0.5"
                    value={bollingerStdDev}
                    onChange={e => setBollingerStdDev(Number(e.target.value))}
                  />
                </label>
              </div>
            )}
          </div>

          <div className={styles.indicatorGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={emaSelected}
                onChange={e => setEmaSelected(e.target.checked)}
              />
              EMA (Exponential Moving Average)
            </label>
            {emaSelected && (
              <div className={styles.indicatorParams}>
                <label>
                  Period:
                  <input
                    type="number"
                    min="2"
                    max="200"
                    value={emaPeriod}
                    onChange={e => setEmaPeriod(Number(e.target.value))}
                  />
                </label>
              </div>
            )}
          </div>
        </div>
      </div>
      <button className={styles.downloadButton} onClick={handleDownload}>
        Download Excel
      </button>
    </div>
  )
}
